import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryKeys";
import { useAppSelector } from "../../store/hooks";
import type { EncoderFhirPrefill } from "../../types/encoder";
import {
  encoderPrefillFromFhir,
  getPatientId,
  getPatientResource,
} from "../../utils/encoder-fhir";
import { useFhirClient } from "../useFhirClient";

const bundleResources = (bundle: unknown, resourceType: string): unknown[] => {
  if (!bundle || typeof bundle !== "object") return [];
  const record = bundle as Record<string, unknown>;
  const entries = Array.isArray(record.entry) ? record.entry : [];
  return entries
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const resource = (entry as { resource?: unknown }).resource;
      if (
        resource &&
        typeof resource === "object" &&
        (resource as { resourceType?: string }).resourceType === resourceType
      ) {
        return resource;
      }
      return null;
    })
    .filter(Boolean);
};

const fetchEncoderFhirPrefill = async (
  client: any,
  patient: unknown,
  encounterId: string | null
): Promise<EncoderFhirPrefill> => {
  const patientId = getPatientId(patient) ?? "";
  const patientResource = getPatientResource(patient);
  let encounterResource: unknown = null;
  const conditions: unknown[] = [];
  const procedures: unknown[] = [];

  if (client && encounterId) {
    try {
      encounterResource = await client.request(
        `Encounter/${encodeURIComponent(encounterId)}`
      );
    } catch (error) {
      console.warn("Failed to load FHIR encounter for encoder:", error);
    }
  }

  if (client && patientId) {
    const conditionQueries = encounterId
      ? [
          `Condition?patient=${encodeURIComponent(patientId)}&encounter=${encodeURIComponent(encounterId)}&_count=50`,
          `Condition?patient=${encodeURIComponent(patientId)}&_count=50`,
        ]
      : [
          `Condition?patient=${encodeURIComponent(patientId)}&_count=50`,
        ];
    for (const query of conditionQueries) {
      try {
        const bundle = await client.request(query);
        const resources = bundleResources(bundle, "Condition");
        if (resources.length) {
          conditions.push(...resources);
          break;
        }
      } catch (error) {
        console.warn("Failed to load FHIR conditions for encoder:", query, error);
      }
    }

    const procedureQueries = encounterId
      ? [
          `Procedure?patient=${encodeURIComponent(patientId)}&encounter=${encodeURIComponent(encounterId)}&_count=50`,
          `Procedure?patient=${encodeURIComponent(patientId)}&_count=50`,
        ]
      : [
          `Procedure?patient=${encodeURIComponent(patientId)}&_count=50`,
        ];
    for (const query of procedureQueries) {
      try {
        const bundle = await client.request(query);
        const resources = bundleResources(bundle, "Procedure");
        if (resources.length) {
          procedures.push(...resources);
          break;
        }
      } catch (error) {
        console.warn("Failed to load FHIR procedures for encoder:", query, error);
      }
    }
  }

  return encoderPrefillFromFhir({
    patient: patientResource,
    encounter: encounterResource,
    conditions,
    procedures,
  });
};

export const useEncoderFhirPrefill = (options?: { enabled?: boolean }) => {
  const client = useFhirClient();
  const patient = useAppSelector((state) => state.fhir.patient);
  const encounter = useAppSelector((state) => state.fhir.encounter);
  const patientId = getPatientId(patient) ?? "";
  const encounterId = encounter || null;
  const enabled = (options?.enabled ?? true) && Boolean(client && patientId);

  return useQuery({
    queryKey: queryKeys.encoderFhirPrefill(patientId, encounterId),
    queryFn: () => fetchEncoderFhirPrefill(client, patient, encounterId),
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
};
